import React from 'react';
import {
  Card,
  Button,
  Pagination,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { FaFileExcel, FaTable } from 'react-icons/fa';
import moment from 'moment';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { formatarMonetario } from '~/Utils/Helpers';
import EmptyState from './EmptyState';

// W-0928 — Whitelist de colunas sortáveis (PRD §4.2 + R7)
// nome_usuario, codigo_grupo_empresa, vendedor, codigo_conta, saldo

const formatarSaldo = (valor) => {
  if (valor == null) return '-';
  const formatado = formatarMonetario(Math.abs(valor));
  return valor < 0 ? `(${formatado})` : formatado;
};

const formatarData = (data) =>
  data ? moment(data).format('DD/MM/YYYY') : '-';

const COLUNAS = [
  {
    title: 'GE',
    key: 'codigo_grupo_empresa',
    ordenacao: true,
    align: 'left',
    render: (_value, item) => (
      <>
        <strong>{item.codigo_grupo_empresa}</strong>
        {item.apelido_grupo_empresa ? (
          <span className="text-muted ml-1">
            - {item.apelido_grupo_empresa}
          </span>
        ) : null}
      </>
    ),
  },
  {
    title: 'Conta',
    key: 'codigo_conta',
    ordenacao: true,
    align: 'left',
    render: (_value, item) => (
      <>
        <strong>{item.codigo_conta}</strong>
        {item.apelido_conta ? (
          <span className="text-muted ml-1">- {item.apelido_conta}</span>
        ) : null}
      </>
    ),
  },
  { title: 'Tipo', key: 'tipo', align: 'left' },
  { title: 'Status', key: 'status', align: 'center' },
  {
    title: 'Responsável',
    key: 'nome_usuario',
    ordenacao: true,
    align: 'left',
    render: (_value, item) => item.nome_usuario_responsavel || '-',
  },
  {
    title: 'Ativo',
    key: 'codigo_ativo',
    align: 'left',
    render: (_value, item) => (
      <>
        <strong>{item.codigo_ativo}</strong>
        {item.nome_ativo ? (
          <span className="text-muted ml-1">- {item.nome_ativo}</span>
        ) : null}
      </>
    ),
  },
  {
    title: 'Saldo',
    key: 'saldo',
    ordenacao: true,
    align: 'right',
    render: (_value, item) => formatarSaldo(item.saldo),
  },
  {
    title: 'Último Mata',
    key: 'data_mata',
    align: 'center',
    render: (_value, item) => formatarData(item.data_mata),
  },
  {
    title: 'Dias',
    key: 'dias_lancto',
    align: 'right',
    render: (_value, item) =>
      item.dias_lancto != null ? item.dias_lancto : 0,
  },
  {
    title: 'Movtos não conf.',
    key: 'qtde_movtos_nao_conf',
    align: 'right',
    render: (_value, item) =>
      item.qtde_movtos_nao_conf === -1
        ? '—'
        : item.qtde_movtos_nao_conf != null
        ? item.qtde_movtos_nao_conf
        : 0,
  },
  {
    title: 'Vendedor',
    key: 'vendedor',
    ordenacao: true,
    align: 'center',
    render: (_value, item) =>
      item.codigo_vendedor && item.codigo_vendedor !== 0
        ? item.codigo_vendedor
        : '-',
  },
];

const Paginador = ({
  paginaAtual,
  totalPaginas,
  totalRegistros,
  tamanhoPagina,
  itensNaPagina,
  onMudarPagina,
}) => {
  if (totalPaginas <= 1) return null;

  const itens = [];
  const maxBotoes = 5;

  itens.push(
    <Pagination.First
      key="first"
      onClick={() => onMudarPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => onMudarPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );

  let inicio = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
  if (fim - inicio < maxBotoes - 1) {
    inicio = Math.max(1, fim - maxBotoes + 1);
  }

  if (inicio > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }

  for (let n = inicio; n <= fim; n += 1) {
    itens.push(
      <Pagination.Item
        key={`pag-${n}`}
        active={n === paginaAtual}
        onClick={() => onMudarPagina(n)}
      >
        {n}
      </Pagination.Item>
    );
  }

  if (fim < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }

  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => onMudarPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => onMudarPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );

  const inicioRegistro =
    itensNaPagina > 0 ? (paginaAtual - 1) * tamanhoPagina + 1 : 0;
  const fimRegistro = Math.min(
    paginaAtual * tamanhoPagina,
    totalRegistros
  );

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center mt-2 mb-2 px-2">
      <div className="text-muted small">
        Mostrando {inicioRegistro} até {fimRegistro} de {totalRegistros}{' '}
        registros
      </div>
      <Pagination className="mb-0">{itens}</Pagination>
    </div>
  );
};

// Subtotais por agrupamento (PRD §4.2). Server-side já ordena pelo campo do agrupamento.
const calcularSubtotais = (itens, agrupamento) => {
  if (!itens || itens.length === 0) return [];

  let chave;
  let rotulo;
  switch (agrupamento) {
    case 'UsuarioResponsavel':
      chave = 'codigo_usuario_responsavel';
      rotulo = 'nome_usuario_responsavel';
      break;
    case 'Vendedor':
      chave = 'codigo_vendedor';
      rotulo = 'codigo_vendedor';
      break;
    case 'GrupoEmpresaConta':
    default:
      chave = 'codigo_grupo_empresa';
      rotulo = 'apelido_grupo_empresa';
      break;
  }

  const mapa = new Map();
  itens.forEach((item) => {
    const id = item[chave] != null ? String(item[chave]) : '_sem_';
    if (!mapa.has(id)) {
      mapa.set(id, {
        chave: id,
        rotulo: item[rotulo] || '(não informado)',
        qtde: 0,
        somaSaldo: 0,
      });
    }
    const entrada = mapa.get(id);
    entrada.qtde += 1;
    entrada.somaSaldo += Number(item.saldo) || 0;
  });

  return Array.from(mapa.values());
};

const CardResultado = ({
  resultado,
  loading,
  exportando,
  agrupamento,
  paginaAtual,
  tamanhoPagina,
  ordenacao,
  onMudarPagina,
  onMudarOrdenacao,
  onExportar,
}) => {
  if (loading) {
    return (
      <Card className="mb-0">
        <Card.Body className="py-5" style={{ minHeight: 180 }}>
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="text-muted mt-3">Gerando relatório...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!resultado) return null;

  const itens = resultado.itens || [];
  const totalRegistros = resultado.total_registros || 0;
  const totalPaginas = resultado.total_paginas || 1;

  if (itens.length === 0) {
    return <EmptyState />;
  }

  const subtotais = calcularSubtotais(itens, agrupamento);

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <FaTable className="text-primary" />
          <strong>Resultado</strong>
          <Badge variant="info">{totalRegistros}</Badge>
        </div>
        <Button
          variant="success"
          size="sm"
          onClick={onExportar}
          disabled={exportando}
        >
          {exportando ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Exportando...
            </>
          ) : (
            <>
              <FaFileExcel className="mr-1" /> Exportar Excel
            </>
          )}
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <Paginador
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          totalRegistros={totalRegistros}
          tamanhoPagina={tamanhoPagina}
          itensNaPagina={itens.length}
          onMudarPagina={onMudarPagina}
        />

        {subtotais.length > 0 && (
          <div
            className="px-3 py-2 small bg-light border-top border-bottom d-flex flex-wrap"
            style={{ gap: '12px' }}
          >
            <span className="text-muted">Subtotais na página:</span>
            {subtotais.map((s) => (
              <span key={`sub-${s.chave}`}>
                <strong>{s.rotulo}</strong>: {s.qtde}{' '}
                <span className="text-muted">
                  ({formatarMonetario(s.somaSaldo)})
                </span>
              </span>
            ))}
          </div>
        )}

        <div className="table-responsive">
          <Tabela
            colunas={COLUNAS}
            dados={itens.map((item) => ({
              ...item,
              // chave estável composta (LICOES §1.5)
              _key: `${item.codigo_grupo_empresa}-${item.codigo_conta}-${item.codigo_ativo}`,
            }))}
            ordenacao={ordenacao}
            onOrdenacaoChange={onMudarOrdenacao}
            striped
            hover
          />
        </div>

        <Paginador
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          totalRegistros={totalRegistros}
          tamanhoPagina={tamanhoPagina}
          itensNaPagina={itens.length}
          onMudarPagina={onMudarPagina}
        />
      </Card.Body>
    </Card>
  );
};

export default CardResultado;
